import {
  Alert,
  Button,
  Empty,
  Flex,
  Result,
  Spin,
  Steps,
  Typography
} from 'antd'
import { CircleCheck } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ContentSpinner, QueryErrorAlert } from 'src/components'
import { useScopeCatalog } from 'src/services/scopeCatalog'
import { clearScopePreset, saveScopePreset } from 'src/utils/mcpScopePreset'

import { ClientInstructions } from './ClientInstructions'
import { ClientPicker } from './ClientPicker'
import {
  useMcpPolicies,
  useMyMcpConnections,
  useMyMcpServices,
  useUpdateMcpConnectionScopes
} from './hooks'
import {
  connectableServices,
  narrowedScopes,
  newOauthConnection,
  pickedScopes,
  serviceOptions,
  serviceScopeRows
} from './model'
import { ServicePicker } from './ServicePicker'
import { OAUTH_CLIENTS, type McpClientId } from './snippets'
import type { McpServiceId } from './types'

const { Text } = Typography

type Props = {
  workspaceId: number
  isAdmin: boolean
  /** A freshly issued PAT; the drawer owns it so closing can warn before it is lost. */
  token: string | null
  onTokenChange: (token: string | null) => void
  onDone: () => void
}

export const ConnectWizard = ({
  workspaceId,
  isAdmin,
  token,
  onTokenChange,
  onDone
}: Props) => {
  const { t } = useTranslation()
  const services = useMyMcpServices(workspaceId)
  const policies = useMcpPolicies(workspaceId, isAdmin)
  const catalog = useScopeCatalog()
  const updateScopes = useUpdateMcpConnectionScopes(workspaceId, 'mine')
  const [serviceIds, setServiceIds] = useState<McpServiceId[]>([])
  // Untouched services carry every scope; the pick is kept per service.
  const [chosen, setChosen] = useState<
    Partial<Record<McpServiceId, Set<string>>>
  >({})
  const narrowed = useRef(new Set<string>())
  const [clientId, setClientId] = useState<McpClientId | null>(null)
  // Connections that existed when the client was picked; anything new is this client signing in.
  const [knownIds, setKnownIds] = useState<Set<string> | null>(null)

  // Kept in picker order; a service switched off meanwhile simply drops out.
  const picked = connectableServices(services.data ?? []).filter((s) =>
    serviceIds.includes(s.service)
  )
  const several = picked.length > 1
  const rowsFor = (service: McpServiceId) =>
    serviceScopeRows(catalog.data ?? [], service)
  const scopesFor = (service: McpServiceId) =>
    pickedScopes(rowsFor(service), chosen[service])
  const noScopes = picked.filter(
    (s) => rowsFor(s.service).length > 0 && scopesFor(s.service).size === 0
  )
  // A personal token belongs to one service, so the token client needs exactly one.
  const client =
    picked.length && !noScopes.length && !(several && clientId === 'other')
      ? clientId
      : null
  const oauth = !!client && OAUTH_CLIENTS.has(client) && knownIds !== null
  const connections = useMyMcpConnections(workspaceId, oauth)
  const progress = oauth
    ? picked.map((s) => ({
        service: s.service,
        connection: newOauthConnection(
          connections.data ?? [],
          knownIds,
          s.service
        )
      }))
    : []
  const allConnected =
    progress.length > 0 && progress.every((row) => row.connection)
  const waiting = oauth && !allConnected

  // The consent page (same browser) pre-checks exactly these scopes.
  const presetKey = picked
    .map((s) => `${s.service}=${[...scopesFor(s.service)].join(' ')}`)
    .join('|')
  useEffect(() => {
    if (!catalog.data) return
    for (const entry of presetKey ? presetKey.split('|') : []) {
      const [service, codes] = entry.split('=')
      saveScopePreset(workspaceId, service, codes.split(' ').filter(Boolean))
    }
  }, [workspaceId, presetKey, catalog.data])

  // Consent elsewhere never saw the preset: narrow the new connection to the pick.
  useEffect(() => {
    for (const row of progress) {
      const connection = row.connection
      if (!connection || narrowed.current.has(connection.id)) continue
      narrowed.current.add(connection.id)
      const scopes = narrowedScopes(connection.scopes, scopesFor(row.service))
      if (scopes) updateScopes.mutate({ id: connection.id, scopes })
    }
  })

  if (services.isError || policies.isError) {
    return (
      <QueryErrorAlert
        onRetry={() => {
          void services.refetch()
          if (isAdmin) void policies.refetch()
        }}
      />
    )
  }
  if (services.isLoading || (isAdmin && policies.isLoading)) {
    return <ContentSpinner minHeight={240} />
  }

  const options = serviceOptions(
    services.data ?? [],
    isAdmin ? (policies.data ?? []) : null
  )
  if (!options.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <Text type="secondary">
            {t(
              isAdmin
                ? 'workspace.aiConnectors.servicesTab.empty'
                : 'workspace.aiConnectors.connect.noServices'
            )}
          </Text>
        }
      />
    )
  }

  const pickClient = (next: McpClientId | null) => {
    setClientId(next)
    setKnownIds(
      connections.data ? new Set(connections.data.map((c) => c.id)) : null
    )
  }

  const current = !picked.length ? 0 : !client ? 1 : allConnected ? 3 : 2
  const finished = allConnected || !!token

  const handleDone = () => {
    for (const s of picked) clearScopePreset(workspaceId, s.service)
    onDone()
  }

  return (
    <Flex vertical gap={20}>
      <Steps
        size="small"
        current={current}
        items={[
          { title: t('workspace.aiConnectors.connect.serviceStep') },
          { title: t('workspace.aiConnectors.connect.clientStep') },
          { title: t('workspace.aiConnectors.connect.instructionsStep') }
        ]}
      />

      <Flex vertical gap={8}>
        <Text strong>{t('workspace.aiConnectors.connect.chooseService')}</Text>
        {isAdmin ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('workspace.aiConnectors.connect.adminServicesHint')}
          </Text>
        ) : null}
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t('workspace.aiConnectors.connect.scopesHint')}
        </Text>
        <ServicePicker
          workspaceId={workspaceId}
          options={options}
          value={picked.map((s) => s.service)}
          disabled={!!token}
          onChange={setServiceIds}
          catalog={{
            isLoading: catalog.isLoading,
            isError: catalog.isError,
            refetch: () => void catalog.refetch()
          }}
          rowsFor={rowsFor}
          scopesFor={scopesFor}
          onScopesChange={(service, next) =>
            setChosen((prev) => ({ ...prev, [service]: next }))
          }
        />
      </Flex>

      {picked.length ? (
        <Flex vertical gap={8}>
          <Text strong>{t('workspace.aiConnectors.connect.chooseClient')}</Text>
          {several ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('workspace.aiConnectors.connect.tokenOneService')}
            </Text>
          ) : null}
          <ClientPicker
            value={client}
            onChange={pickClient}
            disabled={!!token || noScopes.length > 0}
            isUnavailable={(c) => several && c === 'other'}
          >
            <Flex vertical gap={12}>
              {token ? (
                <Alert
                  type="info"
                  showIcon
                  title={t('workspace.aiConnectors.connect.tokenLocked')}
                />
              ) : null}

              {client && !allConnected ? (
                <ClientInstructions
                  key={`${picked.map((s) => s.service).join(',')}:${client}`}
                  workspaceId={workspaceId}
                  services={picked}
                  client={client}
                  tokenScopes={scopesFor(picked[0].service)}
                  token={token}
                  onTokenChange={onTokenChange}
                />
              ) : null}

              {waiting ? (
                <Alert
                  type="info"
                  showIcon
                  icon={<Spin size="small" />}
                  title={t('workspace.aiConnectors.connect.waitingTitle')}
                  description={
                    <Flex vertical gap={6}>
                      <Text>
                        {t('workspace.aiConnectors.connect.waitingBody')}
                      </Text>
                      {several
                        ? progress.map((row) => (
                            <Flex key={row.service} align="center" gap={6}>
                              {row.connection ? (
                                <CircleCheck
                                  size={14}
                                  color="var(--ant-color-success)"
                                />
                              ) : (
                                <Spin size="small" />
                              )}
                              <Text>
                                {t(
                                  `workspace.aiConnectors.services.${row.service}.name`
                                )}
                              </Text>
                            </Flex>
                          ))
                        : null}
                    </Flex>
                  }
                />
              ) : null}

              {allConnected ? (
                <Result
                  status="success"
                  title={t('workspace.aiConnectors.connect.connectedTitle')}
                  subTitle={t('workspace.aiConnectors.connect.connectedBody', {
                    name: progress[0]?.connection?.name,
                    services: progress
                      .map((row) =>
                        t(`workspace.aiConnectors.services.${row.service}.name`)
                      )
                      .join(', ')
                  })}
                />
              ) : null}
            </Flex>
          </ClientPicker>
        </Flex>
      ) : null}

      {client ? (
        <Flex justify="flex-end">
          <Button type={finished ? 'primary' : 'default'} onClick={handleDone}>
            {t('workspace.aiConnectors.connect.done')}
          </Button>
        </Flex>
      ) : null}
    </Flex>
  )
}
